import * as React from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Checkbox from '@mui/joy/Checkbox';
import Divider from '@mui/joy/Divider';
import Modal from '@mui/joy/Modal';
import ModalClose from '@mui/joy/ModalClose';
import ModalDialog from '@mui/joy/ModalDialog';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import { FileDownloadRounded, PictureAsPdfRounded, TableChartRounded } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface ExportColumn {
  key: string;
  label: string;
  defaultSelected?: boolean;
}

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  /** Column definitions for the export */
  columns: ExportColumn[];
  /** Data rows – each row is a Record<columnKey, cellValue> */
  rows: Record<string, string | number>[];
  /** Base filename without extension */
  filename: string;
  /** Title shown in the PDF header */
  title?: string;
}

export default function ExportModal({ open, onClose, columns, rows, filename, title }: ExportModalProps) {
  const [selected, setSelected] = React.useState<Set<string>>(() => {
    const defaults = new Set<string>();
    columns.forEach((col) => {
      if (col.defaultSelected !== false) defaults.add(col.key);
    });
    return defaults;
  });

  // Re-initialise when columns change (different table opened)
  React.useEffect(() => {
    const defaults = new Set<string>();
    columns.forEach((col) => {
      if (col.defaultSelected !== false) defaults.add(col.key);
    });
    setSelected(defaults);
  }, [columns]);

  const toggleColumn = React.useCallback((key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const selectAll = React.useCallback(() => {
    setSelected(new Set(columns.map((c) => c.key)));
  }, [columns]);

  const selectNone = React.useCallback(() => {
    setSelected(new Set());
  }, []);

  const orderedCols = React.useMemo(
    () => columns.filter((c) => selected.has(c.key)),
    [columns, selected],
  );

  const buildTableData = React.useCallback((): { headers: string[]; body: (string | number)[][] } => {
    const headers = orderedCols.map((c) => c.label);
    const body = rows.map((row) => orderedCols.map((c) => row[c.key] ?? ''));
    return { headers, body };
  }, [orderedCols, rows]);

  const handleExcelExport = React.useCallback(() => {
    const { headers, body } = buildTableData();
    const wsData = [headers, ...body];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Auto-fit column widths
    const colWidths = headers.map((h, colIdx) => {
      const maxLen = Math.max(
        h.length,
        ...body.map((row) => String(row[colIdx] ?? '').length),
      );
      return { wch: Math.min(maxLen + 2, 50) };
    });
    ws['!cols'] = colWidths;

    // Bold the header row
    headers.forEach((_, colIdx) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIdx });
      if (ws[cellRef]) {
        ws[cellRef].s = { font: { bold: true } };
      }
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Nitya Seva');
    XLSX.writeFile(wb, `${filename}.xlsx`);
    onClose();
  }, [buildTableData, filename, onClose]);

  const handlePdfExport = React.useCallback(() => {
    const { headers, body } = buildTableData();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

    if (title) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(title, 14, 14);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text(`Exported on ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`, 14, 20);
      doc.setTextColor(0);
    }

    autoTable(doc, {
      head: [headers],
      body: body as (string | number)[][],
      startY: title ? 25 : 10,
      styles: {
        fontSize: 7.5,
        cellPadding: 2.5,
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [22, 119, 255],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 7.5,
      },
      alternateRowStyles: {
        fillColor: [245, 248, 255],
      },
      margin: { top: 10, left: 10, right: 10 },
    });

    doc.save(`${filename}.pdf`);
    onClose();
  }, [buildTableData, filename, onClose, title]);

  const hasSelection = selected.size > 0;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 'min(96vw, 560px)', maxHeight: '85dvh', display: 'flex', flexDirection: 'column' }}>
        <ModalClose />
        <Typography level="title-lg" startDecorator={<FileDownloadRounded />}>
          Export Data
        </Typography>
        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
          Select the columns to include in the export, then choose a file format.
        </Typography>

        <Divider />

        <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
          <Button size="sm" variant="plain" color="neutral" onClick={selectAll}>Select all</Button>
          <Button size="sm" variant="plain" color="neutral" onClick={selectNone}>Deselect all</Button>
        </Stack>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
            gap: 0.75,
            pr: 0.5,
          }}
        >
          {columns.map((col) => (
            <Checkbox
              key={col.key}
              size="sm"
              label={col.label}
              checked={selected.has(col.key)}
              onChange={() => toggleColumn(col.key)}
            />
          ))}
        </Box>

        <Divider />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="soft"
            color="success"
            startDecorator={<TableChartRounded fontSize="small" />}
            disabled={!hasSelection}
            onClick={handleExcelExport}
          >
            Export Excel
          </Button>
          <Button
            variant="soft"
            color="danger"
            startDecorator={<PictureAsPdfRounded fontSize="small" />}
            disabled={!hasSelection}
            onClick={handlePdfExport}
          >
            Export PDF
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
